import { FC } from "react";

type IconProps = { size?: number; color?: string; className?: string };

/**
 * Diagonal arrow used on publication rows (32px) and the "Request a copy"
 * button (18px). Stroke width is in viewBox units so it scales with `size`.
 */
export const ArrowUpRight: FC<IconProps> = ({
  size = 32,
  color = "#FFFFFF",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.33276 9.33203H22.666V22.6653"
      stroke={color}
      strokeWidth="2.66665"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.33276 22.6653L22.666 9.33203"
      stroke={color}
      strokeWidth="2.66665"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Solid play triangle — Figma "Icon" 45×45 (featured disc) and 25×25 (card discs). */
export const PlayIcon: FC<IconProps> = ({
  size = 25,
  color = "#060B14",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 46 46"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.5005 5.75025L38.3339 23.0003L11.5005 40.2504V5.75025Z"
      fill={color}
      stroke={color}
      strokeWidth="3.75961"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Outlined play glyph for the "Watch" hero button. Figma draws it 12×15, so the
 * viewBox is deliberately non-square and `size` sets the height.
 */
export const PlayOutline: FC<IconProps> = ({
  size = 18,
  color = "#FFFFFF",
  className,
}) => (
  <svg
    className={className}
    width={(size * 14) / 18}
    height={size}
    viewBox="0 0 14 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.875157 0.874969L13.1251 8.74993L0.875157 16.6249V0.874969Z"
      stroke={color}
      strokeWidth="1.70281"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Open-book glyph for the "Read the Books" hero button. Figma: Icon 26×26. */
export const BookIcon: FC<IconProps> = ({
  size = 26,
  color = "#FFFFFF",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 26 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.9998 7.58331V22.75"
      stroke={color}
      strokeWidth="2.16666"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.24971 19.5005C2.96239 19.5005 2.68684 19.3864 2.48368 19.1832C2.28051 18.9801 2.16638 18.7045 2.16638 18.4172V4.33389C2.16638 4.04657 2.28051 3.77102 2.48368 3.56786C2.68684 3.36469 2.96239 3.25056 3.24971 3.25056H8.66637C9.81564 3.25056 10.9178 3.7071 11.7305 4.51976C12.5432 5.33242 12.9997 6.43461 12.9997 7.58388C12.9997 6.43461 13.4562 5.33242 14.2689 4.51976C15.0816 3.7071 16.1838 3.25056 17.333 3.25056H22.7497C23.037 3.25056 23.3125 3.36469 23.5157 3.56786C23.7189 3.77102 23.833 4.04657 23.833 4.33389V18.4172C23.833 18.7045 23.7189 18.9801 23.5157 19.1832C23.3125 19.3864 23.037 19.5005 22.7497 19.5005H16.2497C15.3877 19.5005 14.5611 19.8429 13.9516 20.4524C13.3421 21.0619 12.9997 21.8886 12.9997 22.7505C12.9997 21.8886 12.6573 21.0619 12.0478 20.4524C11.4383 19.8429 10.6117 19.5005 9.7497 19.5005H3.24971Z"
      stroke={color}
      strokeWidth="2.13407"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Audio-signal glyph next to the featured episode host — a centre dot flanked by
 * two arc pairs, not a microphone capsule. Figma "Icon" 18×18.
 */
export const MicIcon: FC<IconProps> = ({
  size = 18,
  color = "rgba(255,255,255,0.7)",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g stroke={color} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.6752 14.3245C0.750209 11.3995 0.750209 6.59956 3.6752 3.67457" strokeWidth="1.49999" />
      <path d="M5.85038 12.1495C4.12538 10.4245 4.12538 7.57447 5.85038 5.77447" strokeWidth="1.49999" />
      <path
        d="M8.99949 10.4995C9.82792 10.4995 10.4995 9.82792 10.4995 8.99949C10.4995 8.17107 9.82792 7.4995 8.99949 7.4995C8.17107 7.4995 7.4995 8.17107 7.4995 8.99949C7.4995 9.82792 8.17107 10.4995 8.99949 10.4995Z"
        strokeWidth="1.46142"
      />
      <path d="M12.1498 5.84965C13.8748 7.57464 13.8748 10.4246 12.1498 12.2246" strokeWidth="1.49999" />
      <path d="M14.3244 3.67457C17.2494 6.59956 17.2494 11.3245 14.3244 14.2495" strokeWidth="1.49999" />
    </g>
  </svg>
);

/** View-count eye in the recording card footer. Figma "Icon" 13×13. */
export const EyeIcon: FC<IconProps> = ({
  size = 13,
  color = "rgba(255,255,255,0.4)",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 13 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g stroke={color} strokeWidth="1.04302" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.11664 6.6891C1.0715 6.56748 1.0715 6.43371 1.11664 6.31209C1.55631 5.24601 2.30264 4.33448 3.26099 3.69307C4.21934 3.05165 5.34656 2.70924 6.49975 2.70924C7.65295 2.70924 8.78017 3.05165 9.73852 3.69307C10.6969 4.33448 11.4432 5.24601 11.8829 6.31209C11.928 6.43371 11.928 6.56748 11.8829 6.6891C11.4432 7.75518 10.6969 8.66671 9.73852 9.30813C8.78017 9.94954 7.65295 10.292 6.49975 10.292C5.34656 10.292 4.21934 9.94954 3.26099 9.30813C2.30264 8.66671 1.55631 7.75518 1.11664 6.6891Z" />
      <path d="M6.49982 8.12483C7.39729 8.12483 8.12483 7.39729 8.12483 6.49982C8.12483 5.60235 7.39729 4.87481 6.49982 4.87481C5.60235 4.87481 4.87481 5.60235 4.87481 6.49982C4.87481 7.39729 5.60235 8.12483 6.49982 8.12483Z" />
    </g>
  </svg>
);

/**
 * Paired quote glyph beside the book pull-quotes. The frame carries the page
 * background so it masks the hairline rule it straddles (Figma: Icon 25×25).
 */
export const QuoteIcon: FC<IconProps> = ({
  size = 25,
  color = "rgba(255,255,255,0.6)",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 26 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="26" height="26" fill="#060B14" />
    <path
      d="M16.4665 5.19959C16.0068 5.19959 15.5659 5.38221 15.2409 5.70727C14.9158 6.03233 14.7332 6.47321 14.7332 6.93292V12.1329C14.7332 12.5926 14.9158 13.0335 15.2409 13.3585C15.5659 13.6836 16.0068 13.8662 16.4665 13.8662C16.6964 13.8662 16.9168 13.9575 17.0793 14.12C17.2419 14.2826 17.3332 14.503 17.3332 14.7329V15.5995C17.3332 16.0592 17.1506 16.5001 16.8255 16.8252C16.5004 17.1502 16.0596 17.3329 15.5999 17.3329C15.37 17.3329 15.1496 17.4242 14.987 17.5867C14.8245 17.7492 14.7332 17.9697 14.7332 18.1995V19.9328C14.7332 20.1627 14.8245 20.3831 14.987 20.5457C15.1496 20.7082 15.37 20.7995 15.5999 20.7995C16.979 20.7995 18.3016 20.2516 19.2768 19.2765C20.252 18.3013 20.7998 16.9786 20.7998 15.5995V6.93292C20.7998 6.47321 20.6172 6.03233 20.2921 5.70727C19.9671 5.38221 19.5262 5.19959 19.0665 5.19959H16.4665Z"
      stroke={color}
      strokeWidth="1.67102"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.93342 5.19959C6.47372 5.19959 6.03284 5.38221 5.70778 5.70727C5.38272 6.03233 5.2001 6.47321 5.2001 6.93292V12.1329C5.2001 12.5926 5.38272 13.0335 5.70778 13.3585C6.03284 13.6836 6.47372 13.8662 6.93342 13.8662C7.16328 13.8662 7.38372 13.9575 7.54625 14.12C7.70878 14.2826 7.80009 14.503 7.80009 14.7329V15.5995C7.80009 16.0592 7.61747 16.5001 7.29241 16.8252C6.96735 17.1502 6.52647 17.3329 6.06676 17.3329C5.83691 17.3329 5.61647 17.4242 5.45394 17.5867C5.29141 17.7492 5.2001 17.9697 5.2001 18.1995V19.9328C5.2001 20.1627 5.29141 20.3831 5.45394 20.5457C5.61647 20.7082 5.83691 20.7995 6.06676 20.7995C7.44588 20.7995 8.76851 20.2516 9.7437 19.2765C10.7189 18.3013 11.2667 16.9786 11.2667 15.5995V6.93292C11.2667 6.47321 11.0841 6.03233 10.7591 5.70727C10.434 5.38221 9.99311 5.19959 9.53341 5.19959H6.93342Z"
      stroke={color}
      strokeWidth="1.67102"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Figma `mingcute:linkedin-line` — an outlined mark, not the solid logo. */
export const LinkedinIcon: FC<IconProps> = ({
  size = 32,
  color = "#FFFFFF",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    /**
     * The glyph path is drawn in the left half of a 0 0 32 32 frame
     * (x -0.49→23.51, y 4.43→28.43 — centre ≈ 11.5, 16.43), so it renders
     * off-centre. Shift the viewBox to put the glyph centre at the box centre.
     */
    viewBox="-4.494 0.43 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.5057 4.43012C20.5665 4.43012 21.5839 4.85154 22.3341 5.60168C23.0842 6.35182 23.5056 7.36922 23.5056 8.43007V24.4299C23.5056 25.4907 23.0842 26.5081 22.3341 27.2583C21.5839 28.0084 20.5665 28.4298 19.5057 28.4298H3.50586C2.44501 28.4298 1.42761 28.0084 0.677471 27.2583C-0.072665 26.5081 -0.494087 25.4907 -0.494087 24.4299V8.43007C-0.494087 7.36922 -0.072665 6.35182 0.677471 5.60168C1.42761 4.85154 2.44501 4.43012 3.50586 4.43012H19.5057ZM19.5057 7.09676H3.50586C3.15225 7.09676 2.81311 7.23723 2.56307 7.48728C2.31302 7.73732 2.17255 8.07645 2.17255 8.43007V24.4299C2.17255 24.7835 2.31302 25.1226 2.56307 25.3727C2.81311 25.6227 3.15225 25.7632 3.50586 25.7632H19.5057C19.8593 25.7632 20.1984 25.6227 20.4485 25.3727C20.6985 25.1226 20.839 24.7835 20.839 24.4299V8.43007C20.839 8.07645 20.6985 7.73732 20.4485 7.48728C20.1984 7.23723 19.8593 7.09676 19.5057 7.09676ZM6.1725 13.7633C6.49907 13.7634 6.81427 13.8833 7.05832 14.1003C7.30236 14.3173 7.45827 14.6163 7.49648 14.9407L7.50582 15.0967V21.7632C7.50544 22.1031 7.37531 22.4299 7.14202 22.6771C6.90873 22.9242 6.58989 23.0729 6.25064 23.0928C5.91139 23.1127 5.57733 23.0023 5.31673 22.7842C5.05613 22.5661 4.88865 22.2567 4.84851 21.9192L4.83918 21.7632V15.0967C4.83918 14.743 4.97966 14.4039 5.2297 14.1539C5.47975 13.9038 5.81888 13.7633 6.1725 13.7633ZM10.1725 12.43C10.4847 12.43 10.7871 12.5395 11.0268 12.7396C11.2666 12.9396 11.4285 13.2175 11.4844 13.5247C11.7527 13.3691 12.0301 13.2297 12.3151 13.1073C13.2044 12.7274 14.5364 12.518 15.739 12.8954C16.3697 13.0953 17.003 13.4687 17.4724 14.1047C17.8924 14.6713 18.119 15.3607 18.1644 16.1353L18.1724 16.43V21.7632C18.172 22.1031 18.0418 22.4299 17.8086 22.6771C17.5753 22.9242 17.2564 23.0729 16.9172 23.0928C16.5779 23.1127 16.2439 23.0023 15.9833 22.7842C15.7227 22.5661 15.5552 22.2567 15.5151 21.9192L15.5057 21.7632V16.43C15.5057 15.99 15.3991 15.7846 15.3297 15.69C15.2302 15.5662 15.0931 15.4782 14.9391 15.4393C14.4751 15.2927 13.8071 15.37 13.3631 15.5593C12.6964 15.8446 12.0858 16.2926 11.6698 16.7073L11.5058 16.8833V21.7632C11.5054 22.1031 11.3753 22.4299 11.142 22.6771C10.9087 22.9242 10.5898 23.0729 10.2506 23.0928C9.91134 23.1127 9.57729 23.0023 9.31668 22.7842C9.05608 22.5661 8.8886 22.2567 8.84847 21.9192L8.83913 21.7632V13.7633C8.83913 13.4097 8.97961 13.0706 9.22965 12.8205C9.4797 12.5705 9.81883 12.43 10.1725 12.43ZM6.1725 9.76339C6.52612 9.76339 6.86525 9.90386 7.1153 10.1539C7.36534 10.404 7.50582 10.7431 7.50582 11.0967C7.50582 11.4503 7.36534 11.7895 7.1153 12.0395C6.86525 12.2895 6.52612 12.43 6.1725 12.43C5.81888 12.43 5.47975 12.2895 5.2297 12.0395C4.97966 11.7895 4.83918 11.4503 4.83918 11.0967C4.83918 10.7431 4.97966 10.404 5.2297 10.1539C5.47975 9.90386 5.81888 9.76339 6.1725 9.76339Z"
      fill={color}
    />
  </svg>
);

/** X (formerly Twitter). Figma comment: "change to X logo". */
export const XIcon: FC<IconProps> = ({
  size = 32,
  color = "#FFFFFF",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill={color}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.9 14.2 28.2 4h-2.2l-8.1 8.9L11.5 4H4l9.7 13.6L4 28h2.2l8.5-9.4L21.5 28H29l-10.1-13.8Zm-3 3.3-1-1.4L7 5.6h3.4l6.3 8.8 1 1.4 8.2 11.5h-3.4l-6.6-9.3Z" />
  </svg>
);

/** Figma `icon-park-outline:dribble` — five stroked vectors on a 32 frame. */
export const DribbbleIcon: FC<IconProps> = ({
  size = 32,
  color = "#FFFFFF",
  className,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g stroke={color} strokeWidth="2.66663" strokeLinecap="round" strokeLinejoin="round">
      <path d="M29.3329 15.9997C29.3351 17.7559 28.9893 19.4951 28.3155 21.1169C27.6416 22.7386 26.653 24.2108 25.4069 25.4482C24.171 26.682 22.704 27.6601 21.0899 28.3267C19.4757 28.9932 17.746 29.3351 15.9997 29.3329C8.63578 29.3329 2.66652 23.3636 2.66652 15.9997C2.66185 12.5809 3.97508 9.29187 6.33314 6.81646C7.57735 5.50355 9.07638 4.45832 10.7385 3.74476C12.4006 3.03119 14.1909 2.66429 15.9997 2.66651C17.746 2.66425 19.4757 3.00617 21.0899 3.67271C22.704 4.33926 24.171 5.31736 25.4069 6.55113C26.653 7.7886 27.6416 9.26075 28.3155 10.8825C28.9893 12.5043 29.3351 14.2435 29.3329 15.9997Z" />
      <path d="M29.3328 15.9989C27.3875 15.9989 22.0209 15.2656 17.2176 17.3742C11.9997 19.6655 8.22173 23.2201 6.57509 25.4301" />
      <path d="M10.9998 3.63507C13.0865 5.56105 17.6397 10.4643 19.333 15.3323C21.0264 20.2002 21.653 26.1855 22.0397 27.8888" />
      <path d="M2.84247 14.2234C5.36111 14.3754 12.0284 14.5121 16.295 12.6901C20.5616 10.8681 24.2329 7.51685 25.4922 6.45353M3.7398 20.9654C4.91466 23.8092 7.03356 26.1615 9.73972 27.6259" />
      <path d="M2.66652 15.9997C2.66185 12.5809 3.97508 9.29187 6.33314 6.81646C7.57735 5.50354 9.07638 4.45832 10.7385 3.74475C12.4006 3.03119 14.1909 2.66429 15.9997 2.66651M21.333 3.77583C22.8514 4.44018 24.2329 5.38131 25.4069 6.55113C26.653 7.7886 27.6416 9.26075 28.3155 10.8825C28.9893 12.5043 29.3351 14.2435 29.3329 15.9997C29.3329 17.641 29.0362 19.2136 28.4929 20.6663M15.9997 29.3329C17.746 29.3351 19.4757 28.9932 21.0899 28.3267C22.704 27.6601 24.171 26.682 25.4069 25.4482" />
    </g>
  </svg>
);
